import { UnknownAction } from "redux"
import { combineReducers, configureStore } from "@reduxjs/toolkit"
import { ThunkAction } from "redux-thunk"
import {
  createTransform,
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist"
import { PersistPartial } from "redux-persist/es/persistReducer"
import storage from "redux-persist-indexeddb-storage"
import nativeStorage from "../lib/ReactNativeStorage"
import chatsReducer from "./chatsSlice"
import messagesReducer from "./messagesSlice"
import pinnedReducer from "./pinnedSlice"
import chatMetaReducer from "./chatMetaSlice"
import connectionStatusReducer from "./connectionStatusSlice"
import profilesReducer from "./profilesSlice"
import settingsReducer from "./settingsSlice"
import appVersionReducer from "./appVersionSlice"
import subjectsReducer from "./subjectsSlice"
import routesReducer from "./routesSlice"
import routes2gReducer from "./routes2gSlice"
import activityReducer from "./activitySlice"
import chatRepliesReducer from "./chatRepliesSlice"
import composerDraftsReducer from "./composerDraftsSlice"
import composerTranslationReducer from "./composerTranslationSlice"
import roomSyncStatusReducer from "./roomSyncStatusSlice"
import sendoutQueueReducer from "./sendoutQueueSlice"
import roomSyncListener from "../listeners/roomSync"
import agentTurnExpiryListener from "../listeners/agentTurnExpiry"
import rosterReducer, { rosterUpdateListener } from "./rosterSlice"
import locationsReducer from "./locationsSlice"
import trailerLocationsReducer from "./trailerLocationsSlice"
import overlaysReducer from "./overlaysSlice"
import seenChatsSlice from "./seenChatsSlice"
import communicatorContactsSlice from "./communicatorContactsSlice"
import accountChatsSlice from "./accountChatsSlice"
import userChatsSlice from "./userChatsSlice"
import tachosSlice from "./tachosSlice"
import deliveryOrderExportStatusesSlice from "./deliveryOrderExportStatusesSlice"
import referenceRouteStatusesSlice from "./referenceRouteStatusesSlice"
import chatsSettingsSlice from "@/reducers/chatsSettingsSlice"
import { mapUpdateListener } from "@/components/maps/RouteView"
import Batcher from "../middlewares/batcher"
import gpsConfigSlice from "@/reducers/gpsConfigSlice"
import tabsSlice from "@/reducers/tabsSlice"
import carsSlice from "@/reducers/carsSlice"
import openclawWorkflowsReducer from "@/reducers/openclawWorkflowsSlice"
import rttReducer from "./rttSlice"
import agentPipelineReducer from "./agentPipelineSlice"
import plannedOrdersReducer from "./plannedOrdersSlice"
import serviceConfigReducer from "./serviceConfigSlice"
import permissionsReducer from "./permissionsSlice"

export const STATE_VERSION = 83
export const TRANSIENT_PERSIST_BLACKLIST = ["rtt", "agentPipeline"] as const
export const PERSIST_BLACKLIST = [
  "roomSyncStatus",
  "overlays",
  "tabs",
  "composerDrafts",
  "aiWidgetMap",
  "dispatcherChats",
  // Read straight from another dispatcher's archive on demand; keeping it would
  // only show a stale "last spoke" after a reload.
  "foreignChatActivity",
  // Pausing translation is for one conversation, not a saved preference, and
  // a failed send must not come back as a stale bar after a reload.
  "composerTranslation",
  ...TRANSIENT_PERSIST_BLACKLIST,
]
import aiWidgetMapReducer from "@/reducers/aiWidgetMapSlice"
import dispatcherChatsReducer from "@/reducers/dispatcherChatsSlice"
import foreignChatActivityReducer from "@/reducers/foreignChatActivitySlice"

export const stripTransientStreamingState = <T extends object | undefined>(
  state: T,
): T => {
  if (!state) {
    return state
  }

  const nextState = { ...state } as Record<string, unknown>
  for (const key of TRANSIENT_PERSIST_BLACKLIST) {
    delete nextState[key]
  }

  return nextState as T
}

const rootReducer = combineReducers({
  chats: chatsReducer,
  messages: messagesReducer,
  pinned: pinnedReducer,
  chatMeta: chatMetaReducer,
  connectionStatus: connectionStatusReducer,
  profiles: profilesReducer,
  settings: settingsReducer,
  overlays: overlaysReducer,
  appVersion: appVersionReducer,
  subjects: subjectsReducer,
  routes: routesReducer,
  routes2g: routes2gReducer,
  activities: activityReducer,
  chatReplies: chatRepliesReducer,
  composerDrafts: composerDraftsReducer,
  composerTranslation: composerTranslationReducer,
  roomSyncStatus: roomSyncStatusReducer,
  sendoutQueue: sendoutQueueReducer,
  roster: rosterReducer,
  locations: locationsReducer,
  trailerLocations: trailerLocationsReducer,
  seenChats: seenChatsSlice,
  communicatorContacts: communicatorContactsSlice,
  accountChats: accountChatsSlice,
  userChats: userChatsSlice,
  tachos: tachosSlice,
  deliveryOrderExportStatuses: deliveryOrderExportStatusesSlice,
  referenceRouteStatuses: referenceRouteStatusesSlice,
  chatsSettings: chatsSettingsSlice,
  gpsConfigs: gpsConfigSlice,
  tabs: tabsSlice,
  cars: carsSlice,
  openclawWorkflows: openclawWorkflowsReducer,
  rtt: rttReducer,
  agentPipeline: agentPipelineReducer,
  aiWidgetMap: aiWidgetMapReducer,
  plannedOrders: plannedOrdersReducer,
  dispatcherChats: dispatcherChatsReducer,
  foreignChatActivity: foreignChatActivityReducer,
  serviceConfig: serviceConfigReducer,
  permissions: permissionsReducer,
})

export type RootState = ReturnType<typeof rootReducer>
export type PersistedRootState = RootState & PersistPartial

type Routes2gState = RootState["routes2g"]

const withoutPastStops = (state: Routes2gState): Routes2gState =>
  state?.routes?.some((route) => route.pastStops !== undefined)
    ? {
        ...state,
        routes: state.routes.map((route) => {
          if (route.pastStops === undefined) return route
          const stored = { ...route }
          delete stored.pastStops
          return stored
        }),
      }
    : state

// Past stops come back with every route fetch and only feed the map overlay,
// so they are kept out of IndexedDB. Dropped on the way in, and on the way
// out too so a store written before this transform loses them on the next
// rehydrate, without a STATE_VERSION purge.
export const routes2gPersistTransform = createTransform<
  Routes2gState,
  Routes2gState
>(withoutPastStops, withoutPastStops, { whitelist: ["routes2g"] })

const migrator = async (state: any, version: number) => {
  const oldVersion = state?._persist?.version
  console.info(`Store migration, ${oldVersion} => ${version}`)

  // Don't alter store if no version is found for some reason
  if (!oldVersion) {
    return stripTransientStreamingState(state)
  }

  // Don't alter store if "old" version is actually newer or same.
  // This is the main path: `oldVersion === version`
  if (oldVersion >= version) {
    return stripTransientStreamingState(state)
  }

  // Just reset the store fully if old version changed
  // This is the default migration logic
  window.analytics.track("PurgeStore", {
    reason: "state_version_changed",
    oldVersion,
    newVersion: version,
  })
  return {} as any
}

export type AppStore = ReturnType<typeof setupStore>

const indexDBName = "slickshift"
const persistConfig = {
  key: "root",
  storage: window.ReactNativeWebView
    ? nativeStorage(window.ReactNativeWebView)
    : storage(indexDBName),
  throttle: 4000,
  version: STATE_VERSION,
  migrate: migrator,
  blacklist: PERSIST_BLACKLIST,
  transforms: [routes2gPersistTransform],
}

export const deleteIndexDB = async (): Promise<void> => {
  try {
    await new Promise((resolve, reject) => {
      const dbDeleteRequest = indexedDB.deleteDatabase(indexDBName)

      dbDeleteRequest.onsuccess = () => {
        console.log("IndexedDB deleted successfully")
        resolve(undefined)
      }

      dbDeleteRequest.onerror = () => {
        console.error("Error occurred while deleting the IndexedDB")
        reject(new Error("Error occurred while deleting the IndexedDB"))
      }

      dbDeleteRequest.onblocked = () => {
        console.warn(
          "Delete request is blocked. Make sure no other tabs are open that use this database.",
        )
        reject(new Error("Delete request is blocked"))
      }
    })
  } catch (error) {
    console.error("Failed to delete IndexedDB:", error)
    throw error // Re-throw the error for further handling if needed
  }
}

const persistedReducer = persistReducer<RootState>(persistConfig, rootReducer)

export const setupStore = (preloadedState?: PersistedRootState) => {
  return configureStore({
    reducer: persistedReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) => {
      const batcher = Batcher(!import.meta.env.DEV, ["chats/changeActiveChat"])
      return getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat([
        roomSyncListener.middleware,
        agentTurnExpiryListener.middleware,
        mapUpdateListener.middleware,
        rosterUpdateListener.middleware,
        batcher,
      ])
    },
    enhancers: (getDefaultEnhancers) => {
      const defaultEnhancers = getDefaultEnhancers({
        autoBatch: { type: "timer", timeout: 200 },
      })
      return defaultEnhancers
    },
  })
}

export type AppDispatch = AppStore["dispatch"]
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  UnknownAction
>
